///
/// EarthBrowser
///
/// Created by Matt Giger on 11/12/2023
/// Copyright (c) 2023 Geodata Labs
///

import { frustum } from "./frustum"
import { round } from "./math"
import { plane } from "./plane"
import { type quaternion } from "./quaternion"
import { vec3, vec4 } from "./vector"

/**
 * Represents a 4x4 matrix used for 3D transformations.
 */
export class matrix {
	/** The identity matrix. */
	public static readonly identity = new matrix([1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1])

	/** The underlying Float32Array storing the matrix elements */
	private readonly array: Float32Array

	/** Public access to the array FOR WebGL ONLY PLEASE */
	public glArrayAccess(): Float32Array { return this.array }

	/**
	 * Creates a copy of the matrix array.
	 * @returns A new Float32Array containing the matrix elements.
	 */
	public arrayCopy(): Float32Array {
		return new Float32Array(this.array)
	}

	/**
	 * Creates a new matrix.
	 * @param value - An array of 16 numbers representing the matrix elements. Defaults to identity matrix.
	 */
	constructor(value: Float32Array | number[] = [1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1]) {
		this.array = (value instanceof Float32Array) ? value : new Float32Array(value)
	}

	/**
	 * Creates a translation matrix.
	 * @param t - Either a vec3 representing the translation vector, or the x component of the translation.
	 * @param y - The y component of the translation (if t is a number).
	 * @param z - The z component of the translation (if t is a number).
	 * @returns A new matrix representing the translation.
	 */
	public static translate(x: vec3 | number, y?: number, z?: number): matrix {
		const array = new Float32Array(16)
		array[0] = 1
		array[5] = 1
		array[10] = 1
		array[15] = 1
		if(x instanceof vec3) {
			array[12] = x.x
			array[13] = x.y
			array[14] = x.z
		} else if(y !== undefined && z !== undefined) {
			array[12] = x
			array[13] = y
			array[14] = z
		}
		return new matrix(array)
	}

	/**
	 * Creates a scaling matrix.
	 * @param s - Either a vec3 representing the scale vector, or the x component of the scale.
	 * @param y - The y component of the scale (if s is a number).
	 * @param z - The z component of the scale (if s is a number).
	 * @returns A new matrix representing the scaling transformation.
	 */
	public static scale(s: vec3 | number[] | number, y?: number, z?: number): matrix {
		const out = new Float32Array(16)
		out[15] = 1
		if(s instanceof vec3) {
			// If s is a vec3, use its components for scaling
			out[0] = s.x
			out[5] = s.y
			out[10] = s.z
		} else if(Array.isArray(s)) {
			out[0] = s[0]
			out[5] = s[1]
			out[10] = s[2]
		} else if(y !== undefined && z !== undefined) {
			// If individual x, y, z components are provided
			out[0] = s
			out[5] = y
			out[10] = z
		} else if(typeof s === 'number') {
			// If a single number is provided, use it for uniform scaling
			out[0] = s
			out[5] = s
			out[10] = s
		} else {
			// If insufficient parameters are provided, return an identity matrix
			return matrix.identity
		}
		return new matrix(out)
	}

	/**
	 * Creates a rotation matrix around an arbitrary axis.
	 * @param normalAxis - A normalized vector representing the axis of rotation.
	 * @param angle - The rotation angle in radians.
	 * @returns A new matrix representing the rotation around the specified axis.
	 */
	public static axisRotation(normalAxis: vec3, angle: number): matrix {
		const x = normalAxis.x
		const y = normalAxis.y
		const z = normalAxis.z
		const s = Math.sin(angle)
		const c = Math.cos(angle)
		const t = 1 - c

		// Perform rotation-specific matrix multiplication
		const out = new Float32Array(16)
		out[0] = x*x*t + c
		out[1] = y*x*t + z*s
		out[2] = z*x*t - y*s
		out[4] = x*y*t - z*s
		out[5] = y*y*t + c
		out[6] = z*y*t + x*s
		out[8] = x*z*t + y*s
		out[9] = y*z*t - x*s
		out[10] = z*z*t + c
		out[15] = 1
		return new matrix(out)
	}

	/**
	 * Multiplies two matrices together.
	 * @param a - The first matrix to multiply.
	 * @param b - The second matrix to multiply.
	 * @returns A new matrix that is the result of multiplying a and b.
	 */
	public multiply(b: matrix): matrix {
		return this.multiplyInto(b, new matrix(new Float32Array(16)))
	}

	/**
	 * Multiplies into an existing matrix instead of allocating one, for
	 * per-frame caches that own their result (RenderState.pushTransform
	 * recomputes every visible node's view products each frame the camera
	 * moves). `out` is overwritten and returned; it may not alias `this`
	 * or `b`. Matrices are otherwise immutable, so only use this on a
	 * matrix nothing else holds a reference to.
	 */
	public multiplyInto(b: matrix, out: matrix): matrix {
		const aa = this.array
		const a00 = aa[0], a01 = aa[1], a02 = aa[2], a03 = aa[3]
		const a10 = aa[4], a11 = aa[5], a12 = aa[6], a13 = aa[7]
		const a20 = aa[8], a21 = aa[9], a22 = aa[10], a23 = aa[11]
		const a30 = aa[12], a31 = aa[13], a32 = aa[14], a33 = aa[15]

		const bb = b.array
		const b00 = bb[0], b01 = bb[1], b02 = bb[2], b03 = bb[3]
		const b10 = bb[4], b11 = bb[5], b12 = bb[6], b13 = bb[7]
		const b20 = bb[8], b21 = bb[9], b22 = bb[10], b23 = bb[11]
		const b30 = bb[12], b31 = bb[13], b32 = bb[14], b33 = bb[15]

		const o = out.array
		o[0] = a00*b00 + a01*b10 + a02*b20 + a03*b30
		o[1] = a00*b01 + a01*b11 + a02*b21 + a03*b31
		o[2] = a00*b02 + a01*b12 + a02*b22 + a03*b32
		o[3] = a00*b03 + a01*b13 + a02*b23 + a03*b33
		o[4] = a10*b00 + a11*b10 + a12*b20 + a13*b30
		o[5] = a10*b01 + a11*b11 + a12*b21 + a13*b31
		o[6] = a10*b02 + a11*b12 + a12*b22 + a13*b32
		o[7] = a10*b03 + a11*b13 + a12*b23 + a13*b33
		o[8] = a20*b00 + a21*b10 + a22*b20 + a23*b30
		o[9] = a20*b01 + a21*b11 + a22*b21 + a23*b31
		o[10] = a20*b02 + a21*b12 + a22*b22 + a23*b32
		o[11] = a20*b03 + a21*b13 + a22*b23 + a23*b33
		o[12] = a30*b00 + a31*b10 + a32*b20 + a33*b30
		o[13] = a30*b01 + a31*b11 + a32*b21 + a33*b31
		o[14] = a30*b02 + a31*b12 + a32*b22 + a33*b32
		o[15] = a30*b03 + a31*b13 + a32*b23 + a33*b33
		return out
	}

	/**
	 * Pre-multiplies a matrix with another matrix.
	 * @param b - The matrix to pre-multiply with.
	 * @returns A new matrix that is the result of pre-multiplying this matrix with b.
	 */
	public premultiply(b: matrix): matrix {
		return b.multiply(this)
	}

	/**
	 * Calculates the inverse of a 4x4 matrix.
	 *
	 * This function computes the inverse of the input matrix using the adjugate method.
	 * If the matrix is singular (non-invertible), it throws an error.
	 *
	 * @param m - The input 4x4 matrix to be inverted.
	 * @returns A new matrix that is the inverse of the input matrix.
	 * @throws Error if the matrix is singular and cannot be inverted.
	 */
	public invert(): matrix {
		const a = this.array
		const a00 = a[0], a01 = a[1], a02 = a[2], a03 = a[3]
		const a10 = a[4], a11 = a[5], a12 = a[6], a13 = a[7]
		const a20 = a[8], a21 = a[9], a22 = a[10], a23 = a[11]
		const a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15]

		// Calculate cofactors
		const b00 = a00 * a11 - a01 * a10
		const b01 = a00 * a12 - a02 * a10
		const b02 = a00 * a13 - a03 * a10
		const b03 = a01 * a12 - a02 * a11
		const b04 = a01 * a13 - a03 * a11
		const b05 = a02 * a13 - a03 * a12
		const b06 = a20 * a31 - a21 * a30
		const b07 = a20 * a32 - a22 * a30
		const b08 = a20 * a33 - a23 * a30
		const b09 = a21 * a32 - a22 * a31
		const b10 = a21 * a33 - a23 * a31
		const b11 = a22 * a33 - a23 * a32

		// Calculate the determinant
		let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06

		if (!det) {
			throw new Error("Matrix is singular and cannot be inverted")
		}
		det = 1.0 / det

		// Calculate the inverse matrix elements
		const out = new Float32Array(16)
		out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det
		out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * det
		out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * det
		out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * det
		out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * det
		out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * det
		out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * det
		out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * det
		out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * det
		out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * det
		out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det
		out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det
		out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det
		out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det
		out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det
		out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det
		return new matrix(out)
	}

	/**
	 * Translates the matrix by the given x, y, and z values.
	 * @param x - The x-coordinate to translate by.
	 * @param y - The y-coordinate to translate by.
	 * @param z - The z-coordinate to translate by.
	 * @returns A new matrix representing the translation.
	 */
	public translateBy(x: number | vec3, y?: number, z?: number): matrix {
		if(x instanceof vec3) {
			return this.multiply(matrix.translate(x))
		} else {
			return this.multiply(matrix.translate(x, y, z))
		}
	}

	/**
	 * Scales the matrix by the given x, y, and z values.
	 * @param x - The x-coordinate to scale by.
	 * @param y - The y-coordinate to scale by.
	 * @param z - The z-coordinate to scale by.
	 * @returns A new matrix representing the scaling.
	 */
	public scaleBy(x: number | vec3, y?: number, z?: number): matrix {
		if(x instanceof vec3) {
			return this.multiply(matrix.scale(x))
		} else {
			return this.multiply(matrix.scale(x, y, z))
		}
	}

	/**
	 * Rotates the matrix by the given angle around the given axis.
	 * @param axis - The axis to rotate around.
	 * @param angle - The angle to rotate by.
	 * @returns A new matrix representing the rotation.
	 */
	public rotateBy(axis: vec3, angle: number): matrix {
		return this.multiply(matrix.axisRotation(axis, angle))
	}

	/**
	 * Multiplies a 4x4 matrix by a 4D vector.
	 * This function transforms a 4D vector using a 4x4 matrix.
	 *
	 * @param m - The 4x4 matrix to multiply with.
	 * @param v - The 4D vector to be transformed.
	 * @returns A new vec4 representing the transformed vector.
	 */
	public multVec4(x: number, y: number, z: number, w: number): vec4 {
		const mm = this.array
		const mx = mm[0]*x + mm[4]*y + mm[8]*z + mm[12]*w
		const my = mm[1]*x + mm[5]*y + mm[9]*z + mm[13]*w
		const mz = mm[2]*x + mm[6]*y + mm[10]*z + mm[14]*w
		const mw = mm[3]*x + mm[7]*y + mm[11]*z + mm[15]*w
		return new vec4(mx, my, mz, mw)
	}

	/**
	 * Multiplies a 4x4 matrix by a 3D vector, treating the vector as a 4D vector with a specified w component.
	 * This is useful for transforming 3D points or vectors with a matrix that includes translation.
	 *
	 * @param m - The 4x4 matrix to multiply with.
	 * @param v - The 3D vector to be transformed.
	 * @param w - The w component to use for the vector (typically 1 for points, 0 for directions).
	 * @returns A new vec3 representing the transformed vector.
	 */
	public multVec3(vec: vec3, w: number): vec3 {
		const mm = this.array
		const mx = mm[0]*vec.x + mm[4]*vec.y + mm[8]*vec.z + mm[12]*w
		const my = mm[1]*vec.x + mm[5]*vec.y + mm[9]*vec.z + mm[13]*w
		const mz = mm[2]*vec.x + mm[6]*vec.y + mm[10]*vec.z + mm[14]*w
		return new vec3(mx, my, mz)
	}

	/**
	 * Gets the translation component of the matrix.
	 * @returns A vec3 representing the translation.
	 */
	public get position(): vec3 {
		return new vec3(this.array[12], this.array[13], this.array[14])
	}

	/** The z component of the translation, without allocating a vec3 */
	public get positionZ(): number {
		return this.array[14]
	}

	/** Element-wise equality */
	public equals(other: matrix): boolean {
		const a = this.array
		const b = other.array
		for(let i = 0; i < 16; i++) {
			if(a[i] !== b[i]) {
				return false
			}
		}
		return true
	}

	/**
	 * Gets the scale component of the matrix.
	 * @returns A vec3 representing the scale along each axis.
	 */
	public get scale(): vec3 {
		return new vec3(this.array[0], this.array[5], this.array[10])
	}

	/**
	 * Creates a new frustum from a projection matrix.
	 * Extract and normalize the six frustum planes from the matrix.
	 *
	 * @returns A frustum.
	 */
	public frustum(): frustum {
		const mvp = this.array

		const left = new plane(
			mvp[3] - mvp[0],
			mvp[7] - mvp[4],
			mvp[11] - mvp[8],
			mvp[15] - mvp[12]
		).normalized()

		const right = new plane(
			mvp[3] + mvp[0],
			mvp[7] + mvp[4],
			mvp[11] + mvp[8],
			mvp[15] + mvp[12]
		).normalized()

		const bottom = new plane(
			mvp[3] + mvp[1],
			mvp[7] + mvp[5],
			mvp[11] + mvp[9],
			mvp[15] + mvp[13]
		).normalized()

		const top = new plane(
			mvp[3] - mvp[1],
			mvp[7] - mvp[5],
			mvp[11] - mvp[9],
			mvp[15] - mvp[13]
		).normalized()

		const near = new plane(
			mvp[3] + mvp[2],
			mvp[7] + mvp[6],
			mvp[11] + mvp[10],
			mvp[15] + mvp[14]
		).normalized()

		const far = new plane(
			mvp[3] - mvp[2],
			mvp[7] - mvp[6],
			mvp[11] - mvp[10],
			mvp[15] - mvp[14]
		).normalized()

		return new frustum([left, right, bottom, top, near, far])
	}

	/**
	 * Multiplies a matrix by a quaternion.
	 *
	 * @param m - The input matrix to be multiplied.
	 * @param q - The quaternion to multiply the matrix by.
	 * @returns A new matrix resulting from the multiplication.
	 *
	 * This function performs the following steps:
	 * 1. Extracts components from the quaternion.
	 * 2. Calculates intermediate values for the rotation matrix.
	 * 3. Extracts elements from the input matrix.
	 * 4. Performs matrix multiplication.
	 * 5. Constructs and returns a new matrix with the result.
	 *
	 * Note: This operation is equivalent to rotating the matrix by the quaternion.
	 */
	public multiplyQuat(q: quaternion): matrix {
		const ma = this.array
		const x = q.x
		const y = q.y
		const z = q.z
		const w = q.w
		const x2 = x + x
		const y2 = y + y
		const z2 = z + z
		const xx = x * x2
		const xy = x * y2
		const xz = x * z2
		const yy = y * y2
		const yz = y * z2
		const zz = z * z2
		const wx = w * x2
		const wy = w * y2
		const wz = w * z2
		const m00 = 1 - (yy + zz)
		const m01 = xy - wz
		const m02 = xz + wy
		const m10 = xy + wz
		const m11 = 1 - (xx + zz)
		const m12 = yz - wx
		const m20 = xz - wy
		const m21 = yz + wx
		const m22 = 1 - (xx + yy)
		const a00 = ma[0]
		const a01 = ma[1]
		const a02 = ma[2]
		const a03 = ma[3]
		const a10 = ma[4]
		const a11 = ma[5]
		const a12 = ma[6]
		const a13 = ma[7]
		const a20 = ma[8]
		const a21 = ma[9]
		const a22 = ma[10]
		const a23 = ma[11]
		const b00 = a00 * m00 + a01 * m10 + a02 * m20
		const b01 = a00 * m01 + a01 * m11 + a02 * m21
		const b02 = a00 * m02 + a01 * m12 + a02 * m22
		const b03 = a03
		const b10 = a10 * m00 + a11 * m10 + a12 * m20
		const b11 = a10 * m01 + a11 * m11 + a12 * m21
		const b12 = a10 * m02 + a11 * m12 + a12 * m22
		const b13 = a13
		const b20 = a20 * m00 + a21 * m10 + a22 * m20
		const b21 = a20 * m01 + a21 * m11 + a22 * m21
		const b22 = a20 * m02 + a21 * m12 + a22 * m22
		const b23 = a23
		return new matrix([
			b00, b01, b02, b03,
			b10, b11, b12, b13,
			b20, b21, b22, b23,
			ma[12], ma[13], ma[14], ma[15]
		])
	}

	/**
	 * Calculates the scaling factor of the matrix, which is the largest component of the scaling vector.
	 * @returns The scaling factor of the matrix.
	 */
	public scalingFactor(): number {
		const m = this.array
		const sx = m[0] * m[0] + m[1] * m[1] + m[2] * m[2]
		const sy = m[4] * m[4] + m[5] * m[5] + m[6] * m[6]
		const sz = m[8] * m[8] + m[9] * m[9] + m[10] * m[10]
		return Math.sqrt(Math.max(sx, sy, sz))
	}

	/**
	 * Calculates the normal matrix of the matrix.
	 * @returns The normal matrix of the matrix.
	 */
	public normalMatrix(): mat3 {
		const m = this.array
		return new mat3([m[0], m[1], m[2], m[4], m[5], m[6], m[8], m[9], m[10]])
	}

	/**
	 * Converts the matrix to a string representation.
	 * @returns A string representation of the matrix.
	 */
	public toString(): string {
		const m = this.array
		let val = `[${round(m[0],2)}, ${round(m[1],2)}, ${round(m[2],2)}, ${round(m[3],2)}\n`
		val += ` ${round(m[4],2)}, ${round(m[5],2)}, ${round(m[6],2)}, ${round(m[7],2)}\n`
		val += ` ${round(m[8],2)}, ${round(m[9],2)}, ${round(m[10],2)}, ${round(m[11],2)}\n`
		val += ` ${round(m[12],2)}, ${round(m[12],2)}, ${round(m[14],2)}, ${round(m[15],2)}]`
		return val
	}
}

/**
 * Represents a 3x3 matrix.
 */
export class mat3 {
	/** The underlying array storing the matrix elements */
	private readonly array: Float32Array

	/** Public access to the array FOR WebGL ONLY PLEASE */
	public glArrayAccess(): Float32Array { return this.array }

	/**
	 * Creates a new 3x3 matrix.
	 * @param value - An array of 9 numbers representing the matrix elements. Defaults to the identity matrix.
	 */
	constructor(value: number[] = [1,0,0,0,1,0,0,0,1]) {
		if(value.length != 9) {
			throw new Error(`mat3 requires an initial length of 9, not ${value.length}`)
		}
		this.array = new Float32Array(value)
	}

	/**
	 * Returns a string representation of the matrix.
	 * @returns A string representing the matrix, with elements rounded to 2 decimal places.
	 */
	public toString(): string {
		const m = this.array
		let val = `[${round(m[0],2)}, ${round(m[1],2)}, ${round(m[2],2)}\n`
		val += ` ${round(m[3],2)}, ${round(m[4],2)}, ${round(m[5],2)}\n`
		val += ` ${round(m[6],2)}, ${round(m[7],2)}, ${round(m[8],2)}]`
		return val
	}

	static rotateX(theta: number): mat3 {
		const cos_theta = Math.cos(theta)
		const sin_theta = Math.sin(theta)
		return new mat3([
			1, 0, 0,
			0, cos_theta, sin_theta,
			0, -sin_theta, cos_theta
		])
	}

	static rotateY(theta: number): mat3 {
		const cos_theta = Math.cos(theta)
		const sin_theta = Math.sin(theta)
		return new mat3([
			cos_theta, 0, -sin_theta,
			0, 1, 0,
			sin_theta, 0, cos_theta
		])
	}

	static rotateZ(theta: number): mat3 {
		const cos_theta = Math.cos(theta)
		const sin_theta = Math.sin(theta)
		return new mat3([
			cos_theta, sin_theta, 0,
			-sin_theta, cos_theta, 0,
			0, 0, 1
		])
	}
}

/**
 * Represents a 2x2 matrix.
 */
export class mat2 {
	/** The underlying array storing the matrix elements */
	public array: Float32Array

	/**
	 * Creates a new 2x2 matrix.
	 * @param value - An array of 4 numbers representing the matrix elements. Defaults to the identity matrix.
	 */
	constructor(value: number[] = [1,0,0,1]) {
		if(value.length != 4) {
			throw new Error(`mat2 requires an initial length of 4, not ${value.length}`)
		}
		this.array = new Float32Array(value)
	}

	/**
	 * Returns a string representation of the matrix.
	 * @returns A string representing the matrix, with elements rounded to 2 decimal places.
	 */
	public toString(): string {
		const m = this.array
		return `[${round(m[0],2)}, ${round(m[1],2)}\n ${round(m[2],2)}, ${round(m[3],2)}]`
	}
}